import React, { useState, useEffect } from 'react';
import { MessageSquare, Search, CheckCircle, XCircle, Info, Clock, Mail, User, Loader2 } from 'lucide-react';
import marathiService from '../../services/marathiService';


const MarathiComments = () => {
  const [comments, setComments] = useState([]);
  const [filteredComments, setFilteredComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterContentType, setFilterContentType] = useState('all');
  const [processingId, setProcessingId] = useState(null);

  // Marathi content types mapping
  const contentTypes = {
    blog: 'Blog',
    article: 'Article',
    infomatic: 'Infomatic',
    interview: 'Interview',
    quote: 'Quote',
    speech: 'Speech'
  };

  useEffect(() => {
    fetchComments();
  }, []);

  useEffect(() => {
    filterComments();
  }, [comments, searchTerm, filterStatus, filterContentType]);

  const fetchComments = async () => {
    try {
      setLoading(true);
      // FIXED: Changed from marathiService.marathiBlogs.comments to marathiService.comments
      const response = await marathiService.comments.getAll();
      
      // Handle different response structures
      let commentsData = [];
      if (Array.isArray(response)) {
        commentsData = response;
      } else if (response.data?.data) {
        commentsData = response.data.data;
      } else if (response.data) {
        commentsData = response.data;
      } else if (response) {
        commentsData = response;
      }
      
      setComments(commentsData);
      setFilteredComments(commentsData);
      setError(null);
    } catch (error) {
      console.error('Error fetching comments:', error);
      setError('Failed to load comments. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filterComments = () => {
    let filtered = [...comments];

    if (searchTerm) {
      filtered = filtered.filter(comment => 
        (comment.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (comment.email?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (comment.comment?.toLowerCase() || '').includes(searchTerm.toLowerCase())
      );
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(comment => comment.status === filterStatus);
    }

    if (filterContentType !== 'all') {
      filtered = filtered.filter(comment => comment.contentType === filterContentType);
    }

    setFilteredComments(filtered);
  };

  const handleStatusChange = async (commentId, newStatus) => {
    try {
      setProcessingId(commentId);
      // FIXED: Changed from marathiService.marathiBlogs.comments to marathiService.comments
      await marathiService.comments.updateStatus(commentId, newStatus);
      
      setComments(comments.map(comment => 
        comment._id === commentId ? { ...comment, status: newStatus } : comment
      ));
    } catch (error) {
      console.error('Error updating comment status:', error);
      setError('Failed to update comment status');
    } finally {
      setProcessingId(null);
    }
  };

  const handleDelete = async (commentId) => {
    if (window.confirm('Are you sure you want to delete this comment?')) {
      try {
        setProcessingId(commentId);
        // FIXED: Changed from marathiService.marathiBlogs.comments to marathiService.comments
        await marathiService.comments.delete(commentId);
        setComments(comments.filter(comment => comment._id !== commentId));
      } catch (error) {
        console.error('Error deleting comment:', error);
        setError('Failed to delete comment');
      } finally {
        setProcessingId(null);
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-red-50">
        <Loader2 className="w-8 h-8 text-red-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-red-50">
        <div className="text-red-600 flex items-center gap-2 p-6 bg-red-100 rounded-lg shadow-lg">
          <Info className="w-5 h-5" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-8 bg-red-50 min-h-screen">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-black mb-2">Marathi Comments Management</h1>
        <p className="text-red-700">Manage and moderate user comments on Marathi content</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-xl shadow-lg border border-red-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-700">Total Comments</p>
              <p className="text-2xl font-bold text-black">{comments.length}</p>
            </div>
            <MessageSquare className="w-8 h-8 text-red-600" />
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-xl shadow-lg border border-red-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-700">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">
                {comments.filter(c => c.status === 'pending').length}
              </p>
            </div>
            <Clock className="w-8 h-8 text-yellow-500" />
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-xl shadow-lg border border-red-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-700">Approved</p>
              <p className="text-2xl font-bold text-green-600">
                {comments.filter(c => c.status === 'approved').length}
              </p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-xl shadow-lg border border-red-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-700">Rejected</p>
              <p className="text-2xl font-bold text-red-600">
                {comments.filter(c => c.status === 'rejected').length}
              </p>
            </div>
            <XCircle className="w-8 h-8 text-red-500" />
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-xl shadow-lg border border-red-200 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-red-400" />
            <input
              type="text"
              placeholder="Search comments..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="w-full px-4 py-2 border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>

          <select
            value={filterContentType}
            onChange={(e) => setFilterContentType(e.target.value)}
            className="w-full px-4 py-2 border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
          >
            <option value="all">All Content Types</option>
            {Object.entries(contentTypes).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="space-y-6">
        {filteredComments.length > 0 ? (
          filteredComments.map((comment) => (
            <div 
              key={comment._id || Math.random()} 
              className="bg-white p-6 rounded-xl shadow-lg border border-red-200 hover:shadow-xl transition-shadow duration-300"
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-medium text-black flex items-center gap-2">
                      <User className="w-4 h-4 text-red-500" />
                      {comment.name || 'Anonymous'}
                    </h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(comment.status)}`}>
                      {comment.status || 'pending'}
                    </span>
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      {contentTypes[comment.contentType] || comment.contentType || 'Unknown'}
                    </span>
                  </div>
                  <p className="text-sm text-red-700 flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    {comment.email || 'No email'}
                  </p>
                  <p className="text-sm text-red-700 mt-1">
                    {formatDate(comment.createdAt)}
                  </p>
                </div>
                
                <div className="flex gap-2">
                  {comment.status !== 'approved' && (
                    <button
                      onClick={() => handleStatusChange(comment._id, 'approved')}
                      disabled={processingId === comment._id}
                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors duration-200"
                      title="Approve"
                    >
                      <CheckCircle className="w-5 h-5" />
                    </button>
                  )}
                  
                  {comment.status !== 'rejected' && (
                    <button
                      onClick={() => handleStatusChange(comment._id, 'rejected')}
                      disabled={processingId === comment._id}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                      title="Reject"
                    >
                      <XCircle className="w-5 h-5" />
                    </button>
                  )}
                  
                  <button
                    onClick={() => handleDelete(comment._id)}
                    disabled={processingId === comment._id}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                    title="Delete"
                  >
                    <XCircle className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              <div className="bg-red-50 p-4 rounded-lg">
                <p className="text-gray-700 whitespace-pre-wrap">{comment.comment || 'No comment text'}</p>
              </div>
              
              <div className="mt-4">
                <p className="text-sm text-red-700">
                  Content ID: {comment.contentId || 'Unknown'}
                </p>
              </div>
            </div>
          ))
        ) : (
          <div className="bg-white p-12 rounded-xl shadow-lg border border-red-200 text-center">
            <MessageSquare className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <p className="text-red-700">No comments found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MarathiComments;